import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Fade,
  Zoom
} from '@mui/material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

import { styled, alpha, keyframes } from '@mui/material/styles';
import {
  Code,
  DesignServices,
  Web,
  Security,
  CheckCircle,
  Star,
  RocketLaunch,
  Devices,
  Cloud,
  Verified,
  ArrowForward,
  Speed,
  SupportAgent,
  Groups,
  Timeline,
  Storage,
  Brush,
  Analytics,
  Email,
  Phone,
  Language,
  Palette,
} from '@mui/icons-material';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// Professional color palette
const colors = {
  primary: '#4551be', // Exact blue from your image
  secondary: '#6b77d4', // Lighter blue accent
  success: '#10b981', // Green
  warning: '#f59e0b', // Orange
  dark: '#1e293b', // Dark text
  light: '#f0f4f8', // Light background
  gray: '#64748b', // Slate 500
  accent: '#4551be',
  textSecondary: '#64748b',
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const WebsiteDevelopment = () => {
  const theme = useTheme();

  const services = [
    {
      icon: <Code />,
      title: "Custom Web Development",
      description: "Enterprise-grade web applications built with modern frameworks and best practices for scalability and performance.",
      color: colors.primary,
      features: ["React/Next.js Applications", "Node.js Backend", "RESTful APIs", "Database Design"]
    },
    {
      icon: <Palette />,
      title: "UI/UX Design",
      description: "Beautiful, intuitive interfaces that engage users and strengthen your brand identity across all platforms.",
      color: colors.secondary,
      features: ["User Research", "Wireframing", "Interactive Prototypes", "Design Systems"]
    },
    {
      icon: <Web />,
      title: "E-Commerce Solutions",
      description: "Feature-rich online stores with secure payment gateways, inventory management and seamless user experience.",
      color: colors.success,
      features: ["Payment Integration", "Product Management", "Shopping Cart", "Order Tracking"]
    }
  ];

  const features = [
    {
      icon: <Speed />,
      title: "Lightning Fast Performance",
      description: "Optimized code and assets for sub-second load times",
      color: colors.primary
    },
    {
      icon: <Security />,
      title: "Enterprise Security",
      description: "SSL encryption, XSS protection and secure authentication",
      color: colors.secondary
    },
    {
      icon: <SupportAgent />,
      title: "24/7 Technical Support",
      description: "Round-the-clock maintenance and quick issue resolution",
      color: colors.success
    },
    {
      icon: <Devices />,
      title: "Fully Responsive",
      description: "Perfect viewing experience on all devices",
      color: colors.warning
    },
    {
      icon: <Timeline />,
      title: "SEO Optimized",
      description: "Built with best practices for search engine visibility",
      color: colors.primary
    },
    {
      icon: <Cloud />,
      title: "Cloud Hosting",
      description: "Scalable infrastructure on AWS, Google Cloud or Azure",
      color: colors.secondary
    }
  ];

  const whyChooseUs = [
    {
      icon: <Verified />,
      title: "Proven Track Record",
      points: ["10+ years in web development", "500+ websites launched", "Clients in 20+ countries"]
    },
    {
      icon: <Groups />,
      title: "Expert Development Team",
      points: ["Senior full-stack developers", "UI/UX design specialists", "Project managers with 8+ years experience"]
    },
    {
      icon: <RocketLaunch />,
      title: "Fast Time-to-Market",
      points: ["Agile development methodology", "MVP in 4-6 weeks", "Iterative improvements"]
    },
    {
      icon: <SupportAgent />,
      title: "Ongoing Support",
      points: ["Free 30-day maintenance", "Regular security updates", "Performance monitoring"]
    },
    {
      icon: <Brush />,
      title: "Quality Assurance",
      points: ["100% code review", "Cross-browser testing", "Automated testing suite"]
    },
    {
      icon: <Analytics />,
      title: "Data-Driven Approach",
      points: ["Google Analytics integration", "User behavior tracking", "Conversion optimization"]
    }
  ];

 const technologies = [
    { name: "React js", icon: "⚛️", color: "#61DAFB" },
    { name: "Next.js", icon: "⏭️", color: "#000000" },
    { name: "TypeScript", icon: "📘", color: "#3178C6" },
    { name: "Node.js", icon: "🟢", color: "#339933" },
    { name: "Python", icon: "🐍", color: "#3776AB" },
    { name: "Java", icon: "☕", color: "#ED8B00" },
    { name: "Spring Boot", icon: "🍀", color: "#6DB33F" },
    { name: "MongoDB", icon: "🍃", color: "#47A248" },
    { name: "MySQL", icon: "🐬", color: "#4479A1" },
    { name: "AWS", icon: "☁️", color: "#FF9900" },
    { name: "Docker", icon: "🐳", color: "#2496ED" },
    { name: "GitHub", icon: "🔀", color: "#F05032" },
    { name: "Firebase", icon: "🔥", color: "#FFCA28" },
    { name: "Figma", icon: "🎨", color: "#F24E1E" }
  ];

  const process = [
    { step: "01", title: "Discovery", description: "We analyze your requirements and create a detailed project roadmap" },
    { step: "02", title: "Design", description: "We create wireframes and interactive prototypes for your approval" },
    { step: "03", title: "Development", description: "Our developers build your website using modern technologies" },
    { step: "04", title: "Testing", description: "Rigorous testing across devices and browsers" },
    { step: "05", title: "Launch", description: "We deploy your website and ensure everything runs smoothly" },
    { step: "06", title: "Support", description: "Ongoing maintenance and updates to keep your site secure" }
  ];

  return (
    <Box sx={{ bgcolor: colors.light }}>
      <Navbar />

      <Box
  sx={{
    minHeight: { xs: 'auto', md: '60vh' },
    display: 'flex',
    alignItems: 'center',

    // Navbar overlap fix
    pt: { xs: 13, sm: 12, md: 8 },
    pb: { xs: 7, sm: 9, md: 12 },

    background: `linear-gradient(
      135deg,
      ${alpha(colors.primary, 0.03)} 0%,
      ${alpha(colors.secondary, 0.03)} 100%
    )`,

    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  }}
>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<Code />}
                    label="⚡ WEB DEVELOPMENT AGENCY"
                    sx={{
                      mb: 3,
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  {/* Title Changed to Match Bower Cred Image Colour */}
                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: '#4551be', // <--- EXACT COLOUR FROM YOUR IMAGE
                  }}>
                    Custom Website
                    <Box component="span" sx={{
                      display: 'block',
                      color: '#4551be', // <--- EXACT COLOUR FROM YOUR IMAGE
                    }}>
                      Development Services
                    </Box>
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary, // Dark grey
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    We build high-performance, responsive websites that drive business growth.
                    From custom web applications to e-commerce solutions, our expert team delivers
                    exceptional digital experiences tailored to your needs.
                  </Typography>


                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '250px', md: '350px' },
                    height: { xs: '250px', md: '350px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    margin: '0 auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <Language sx={{
                      fontSize: { xs: 100, md: 150 },
                      color: colors.primary
                    }} />
                  </Box>

                  {/* Floating Elements */}
                  <Box sx={{
                    position: 'absolute',
                    top: '15%',
                    right: '10%',
                    width: 60,
                    height: 60,
                    borderRadius: '15px',
                    background: alpha(colors.success, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 7s ease-in-out infinite`,
                    animationDelay: '1s'
                  }}>
                    <Code sx={{ color: colors.success }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '5%',
                    width: 50,
                    height: 50,
                    borderRadius: '10px',
                    background: alpha(colors.warning, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '2s'
                  }}>
                    <DesignServices sx={{ color: colors.warning }} />
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    top: '30%',
                    left: '15%',
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: alpha(colors.secondary, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 9s ease-in-out infinite`,
                    animationDelay: '1.5s'
                  }}>
                    <Storage sx={{ color: colors.secondary, fontSize: 25 }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="OUR PROCESS"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            How We Build Your Website
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            A structured approach to deliver high-quality results on time
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {process.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Typography variant="h2" sx={{
                  position: 'absolute',
                  top: 10,
                  right: 20,
                  fontSize: '4rem',
                  fontWeight: 900,
                  color: alpha(colors.primary, 0.1),
                  lineHeight: 1
                }}>
                  {item.step}
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ color: colors.textSecondary }}>
                    {item.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="WHY CHOOSE US"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              We Deliver Excellence
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
              Here's why industry leaders trust us with their web development projects
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {whyChooseUs.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                  }
                }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: colors.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'white'
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                    {item.title}
                  </Typography>
                  <Stack spacing={1}>
                    {item.points.map((point, idx) => (
                      <FeatureItem key={idx}>
                        <CheckCircle sx={{ fontSize: 16, color: colors.success }} />
                        <Typography variant="body2" color="text.secondary" sx={{ color: colors.textSecondary }}>
                          {point}
                        </Typography>
                      </FeatureItem>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="OUR SERVICES"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            What We Offer
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            Comprehensive web development solutions tailored to your business needs
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ServiceCard>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    background: colors.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'white'
                  }}>
                    {service.icon}
                  </Box>

                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: colors.dark }}>
                    {service.title}
                  </Typography>

                  <Typography color="text.secondary" paragraph sx={{ color: colors.textSecondary }}>
                    {service.description}
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {service.features.map((feature, idx) => (
                      <FeatureItem key={idx}>
                        <CheckCircle sx={{ fontSize: 16, color: colors.success }} />
                        <Typography variant="body2" sx={{ color: colors.dark }}>{feature}</Typography>
                      </FeatureItem>
                    ))}
                  </Stack>
                </CardContent>
              </ServiceCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Grid */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="KEY FEATURES"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Why Our Websites Stand Out
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: colors.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'white'
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: colors.dark }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ color: colors.textSecondary }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Stack */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="TECHNOLOGY STACK"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Modern Tools We Use
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2
        }}>
          {technologies.map((tech, index) => (
            <Chip
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{tech.icon}</span>
                  <span>{tech.name}</span>
                </Box>
              }
              sx={{
                px: 2,
                py: 3,
                fontSize: '1rem',
                bgcolor: 'white',
                boxShadow: 1,
                border: `1px solid ${alpha(tech.color, 0.3)}`,
                color: colors.dark, // Dark text
                '&:hover': {
                  bgcolor: alpha(tech.color, 0.1),
                  cursor: 'pointer'
                }
              }}
            />
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        background: colors.gradient,
        py: 8
      }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: '#ffffff' }}>
            Ready to Build Your Website?
          </Typography>

          <Typography variant="h6" sx={{ color: alpha('#ffffff', 0.9), mb: 4 }}>
            Transform your ideas into powerful web solutions that elevate your brand, engage customers and accelerate growth.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2, sm: 4 },
              mt: 4,
              px: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Email
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, md: 24 },
                }}
              />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                  wordBreak: "break-word",
                  textAlign: "center",
                }}
              >
                lidertechnologysolutions@gmail.com
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Phone
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: 20, md: 24 },
                }}
              />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                }}
              >
                +91-98403 69993
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default WebsiteDevelopment;